import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedBackground from '../components/AnimatedBackground';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import ToastContainer, { useToast } from '../components/ToastContainer';
import { getUser, getAuthToken, saveAuth } from '../services/auth';
import { updateUserProfile } from '../services/api';
import { t, getLanguage } from '../utils/i18n';
import logoImage from '../assets/babysit_logo.png';

const REQUIRED_FIELDS = ['firstName', 'lastName', 'phone', 'role'];

const PROFILE_FIELDS = [
  { key: 'firstName', label: 'Ime', required: true },
  { key: 'lastName', label: 'Prezime', required: true },
  { key: 'phone', label: 'Telefon', required: true },
  { key: 'role', label: 'Uloga', required: true },
  { key: 'hourlyRate', label: 'Satnica', required: false, condition: (form) => form.role === 'babysitter' },
  { key: 'address', label: 'Adresa', required: false },
  { key: 'cities', label: 'Gradovi', required: false },
  { key: 'childBirthYear', label: 'Godina rođenja deteta', required: false, condition: (form) => form.role === 'parent' },
];

const defaultAddress = {
  street: '',
  city: '',
  postalCode: '',
};

// Calculate profile completion percentage
const calculateProfileCompletion = (user, form, address) => {
  const fields = PROFILE_FIELDS.filter(field => {
    if (field.condition && !field.condition(form)) return false;
    return true;
  });

  let completed = 0;
  const missing = [];

  fields.forEach(field => {
    let isCompleted = false;
    
    if (field.key === 'address') {
      isCompleted = !!(address.street || address.city || address.postalCode);
    } else if (field.key === 'cities') {
      const cities = user?.preferences?.cities || [];
      isCompleted = cities.length > 0;
    } else if (field.key === 'childBirthYear') {
      const childBirthYear = user?.preferences?.childBirthYear;
      isCompleted = !!childBirthYear;
    } else if (field.key === 'hourlyRate') {
      isCompleted = !!(form[field.key] && form[field.key].toString().trim());
    } else {
      isCompleted = !!(form[field.key] && form[field.key].toString().trim());
    }

    if (isCompleted) {
      completed++;
    } else {
      missing.push(field.label);
    }
  });

  const percentage = Math.round((completed / fields.length) * 100);
  return { percentage, completed, total: fields.length, missing };
};

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { showToast, toasts, removeToast } = useToast();
  const [user, setUser] = useState(() => getUser());
  const [language, setLanguageState] = useState(getLanguage());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [address, setAddress] = useState(defaultAddress);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    role: 'parent',
    hourlyRate: '',
  });

  useEffect(() => {
    const handleLanguageChange = () => {
      setLanguageState(getLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    if (!user) {
      showToast(t('auth.completeProfile.sessionExpired', language), 'error', 5000);
      navigate('/login', { replace: true });
      return;
    }

    if (user?.profileComplete) {
      navigate('/dashboard', { replace: true });
      return;
    }

    setForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      role: user.role || 'parent',
      hourlyRate: user.hourlyRate ? String(user.hourlyRate) : (user.preferences?.hourlyRateMin ? String(user.preferences.hourlyRateMin) : ''),
    });

    if (user.address) {
      setAddress({
        street: user.address.street || '',
        city: user.address.city || (user.address.cities && user.address.cities[0]) || '',
        postalCode: user.address.postalCode || user.address.zip || '',
      });
    }
  }, [user, navigate, showToast, language]);

  const profileCompletion = useMemo(() => {
    return calculateProfileCompletion(user, form, address);
  }, [user, form, address]);

  const isValid = useMemo(() => {
    return REQUIRED_FIELDS.every((field) => form[field]?.toString().trim());
  }, [form]);

  const handleInputChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleAddressChange = (key, value) => {
    setAddress((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isValid) {
      showToast(t('auth.completeProfile.validationError', language), 'error', 4000);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        role: form.role,
      };

      if (form.role === 'babysitter' && form.hourlyRate) {
        payload.hourlyRate = Number(form.hourlyRate);
      }

      const cleanAddress = {
        street: address.street.trim(),
        city: address.city.trim(),
        postalCode: address.postalCode.trim(),
      };

      if (cleanAddress.street || cleanAddress.city || cleanAddress.postalCode) {
        payload.address = cleanAddress;
      }

      // Preserve existing preferences and merge with any new data
      const currentPreferences = user?.preferences || {};
      const preferences = {
        ...currentPreferences,
        // Keep cities from registration if they exist
        cities: currentPreferences.cities || [],
        customCityOptions: currentPreferences.customCityOptions || [],
        childBirthYear: currentPreferences.childBirthYear,
        hourlyRateMin: currentPreferences.hourlyRateMin,
        hourlyRateMax: currentPreferences.hourlyRateMax,
        jobSearchStatus: currentPreferences.jobSearchStatus,
        isLookingForBabysitter: currentPreferences.isLookingForBabysitter,
        registrationIncomplete: false // Mark as complete after submission
      };
      payload.preferences = preferences;

      const response = await updateUserProfile(payload);
      if (response?.user) {
        const token = getAuthToken();
        if (token) {
          saveAuth(token, response.user);
        }
        setUser(response.user);
        showToast(t('auth.completeProfile.success', language), 'success', 4000);
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 500);
      } else {
        // Invalid response from server
        const errorMessage = 'Invalid response from server';
        console.error('Profile completion error:', errorMessage);
        showToast(t('auth.completeProfile.error', language), 'error', 5000);
        return;
      }
    } catch (error) {
      console.error('Profile completion error:', error);
      const message = error.response?.data?.message || t('auth.completeProfile.error', language);
      showToast(message, 'error', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-100 via-primary-50 to-pink-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900 relative overflow-hidden">
      <AnimatedBackground />

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-6 relative z-10">
        <div className="card w-full max-w-2xl p-8 shadow-xl">
          <div className="flex flex-col items-center text-center mb-8">
            <img src={logoImage} alt="Nannity" className="w-16 h-16 mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('auth.completeProfile.title', language)}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 max-w-lg mb-4">
              {t('auth.completeProfile.subtitle', language)}
            </p>
            
            {/* Profile Completion Progress */}
            <div className="w-full max-w-md mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Popunjenost profila
                </span>
                <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                  {profileCompletion.percentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-3">
                <div 
                  className="bg-primary-600 dark:bg-primary-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${profileCompletion.percentage}%` }}
                />
              </div>
              {profileCompletion.missing.length > 0 && (
                <div className="text-left">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Nedostaje:
                  </p>
                  <ul className="text-xs text-gray-500 dark:text-gray-500 space-y-1">
                    {profileCompletion.missing.map((item, index) => (
                      <li key={index} className="flex items-center">
                        <span className="mr-2">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.completeProfile.firstName', language)}
                </label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.completeProfile.lastName', language)}
                </label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.completeProfile.phone', language)}
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.completeProfile.role', language)}
                </label>
                <select
                  value={form.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  required
                >
                  <option value="parent">{t('auth.completeProfile.roleParent', language)}</option>
                  <option value="babysitter">{t('auth.completeProfile.roleBabysitter', language)}</option>
                </select>
              </div>
            </div>

            {form.role === 'babysitter' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.completeProfile.hourlyRate', language)}
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.hourlyRate}
                  onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder="800"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.completeProfile.street', language)}
                </label>
                <input
                  type="text"
                  value={address.street}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder={t('auth.completeProfile.streetPlaceholder', language)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.completeProfile.city', language)}
                </label>
                <input
                  type="text"
                  value={address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder={t('auth.completeProfile.cityPlaceholder', language)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('auth.completeProfile.postalCode', language)}
                </label>
                <input
                  type="text"
                  value={address.postalCode}
                  onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder={t('auth.completeProfile.postalPlaceholder', language)}
                />
              </div>
            </div>

            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 text-sm text-primary-700 dark:text-primary-200">
              {t('auth.completeProfile.info', language)}
            </div>

            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('auth.completeProfile.saving', language) : t('auth.completeProfile.save', language)}
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}

